import { useEffect, useState } from "react";
import { Form } from "react-bootstrap";

import type { AssignmentPropertyItemRequest, AssignmentPropertyRequest, AssignmentPropertyResponse } from "Src/api/Dto";

import { updateAssignmentPropertyItems } from "Src/squawk/ActionsAssignmentRequest";

interface Props {
  /**
   * Use request for retrieving actual values for assignment properties
   */
  assignmentPropertyRequest: AssignmentPropertyRequest | undefined;

  /**
   * Use response for rendering all possible assignment properties
   */
  assignmentPropertyResponse: AssignmentPropertyResponse;
}

type AssignmentProps = Record<string, string>;

export function AssignmentProperties({ assignmentPropertyResponse, assignmentPropertyRequest }: Props) {
  const { assignmentPropertyItems } = assignmentPropertyResponse;
  const [formAssignmentProps, setFormAssignmentProps] = useState<AssignmentProps>();

  useEffect(() => {
    // Convert assignment property items array to an object for key/value retrieval when rendering the form and the updating state
    const assignmentPropertyItemsObject: AssignmentProps | undefined = assignmentPropertyResponse?.assignmentPropertyItems?.reduce<AssignmentProps>(
      (acc, curr) => {
        const val = assignmentPropertyRequest?.assignmentPropertyItems?.find(
          (assignmentPropertyItemRequest) => assignmentPropertyItemRequest.name === curr.name
        )?.value;
        // Fall back to the response default value (derived from the distribution channel) when no stored value exists
        acc[curr.name] = val ?? curr.value;

        return acc;
      },
      {}
    );

    if (!assignmentPropertyItemsObject) {
      return;
    }

    setFormAssignmentProps(assignmentPropertyItemsObject);
  }, [assignmentPropertyResponse, assignmentPropertyRequest]);

  const handleOnChange = (checked: boolean, assignmentPropertyItemName: string) => {
    const updatedProps = { ...formAssignmentProps, [assignmentPropertyItemName]: checked ? "true" : "false" };
    setFormAssignmentProps(updatedProps);

    const assignmentPropertyItemRequest: AssignmentPropertyItemRequest[] = Object.keys(updatedProps).map((key) => ({
      name: key,
      value: updatedProps[key]
    }));

    updateAssignmentPropertyItems(assignmentPropertyResponse.name, assignmentPropertyItemRequest);
  };

  return (
    <>
      <h4 className="pb-3 pt-2">{assignmentPropertyResponse.description}</h4>
      {assignmentPropertyItems?.map((assignmentProperty) => (
        <Form key={assignmentProperty.name} className="mb-3">
          <Form.Check
            checked={formAssignmentProps ? formAssignmentProps[assignmentProperty.name] === "true" : false}
            label={assignmentProperty.description}
            name={assignmentProperty.name}
            type="checkbox"
            onChange={(e) => handleOnChange(e.target.checked, assignmentProperty.name)}
          />
        </Form>
      ))}
    </>
  );
}

export default AssignmentProperties;
